import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { Box, Button, HStack, IconButton, Popover, Portal, Spacer, Text, VStack } from '@chakra-ui/react'
import { ResetIcon } from '@editor/assets/icons'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { setIsVisible } from '@editor/pages/projects/common/SolidMeshManager/store'
import { patchInspectionArea } from '@editor/services/InspectionArea'
import { decideActionPermission } from '@editor/services/Validation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { patchInspectionAreas, setIsSavingArea } from '@editor/store/page'
import { RangeInput, Tooltip } from '@infraloop/common'
import * as Sentry from '@sentry/react'
import { Chrome } from '@uiw/react-color'
import { ChromeInputType } from '@uiw/react-color-chrome'
import { cloneDeep } from 'lodash'
import { useDebouncedCallback } from 'use-debounce'

import { setIsPcdVisible } from '../../store/editor'
import { patchEditorDisplaySettings } from '../../store/editorSettings'
import { selectDefaultEditorDisplaySettings } from '../../store/selectors'
import LayerItem from '../components/LayerItem'

const Settings: FC = () => {
  // Context
  const { showErrorModal } = useGlobalModalContext()
  const { getAccessToken } = useContext(UserContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const userType = useAppSelector((state) => state.user.userType)
  const isOwner = useAppSelector((state) => state.page.isOwner)
  const isInvited = useAppSelector((state) => state.page.isInvited)
  const isAllowedToModifyDisplaySettings = decideActionPermission(
    isOwner,
    isInvited,
  ).EDITOR.MODIFY_DISPLAY_SETTINGS.includes(userType)

  const labelFontSize = useAppSelector((state) => state.toolUtil.core.labelFontSize)
  const isPointCloudVisible = useAppSelector((state) => state.editor.isPcdVisible)

  const backgroundColor = useAppSelector((state) => state.editorSettings.display.general.bg_color)
  const pcdTransparency = useAppSelector((state) => state.editorSettings.display.pcd.opacity)
  const pointSize = useAppSelector((state) => state.editorSettings.display.pcd.point_size)
  const displaySettings = useAppSelector((state) => state.editorSettings.display)
  const defaultDisplaySettings = useAppSelector(selectDefaultEditorDisplaySettings)

  const solidMeshTransparency = useAppSelector((state) => state.editorSettings.display.mesh.opacity)
  const isMeshFilePresent = useAppSelector((state) => state.solidMesh.isPresent)
  const isMeshFileVisible = useAppSelector((state) => state.solidMesh.isVisible)

  // Keep ref of editor settings to avoid re-triggering effects
  const displaySettingsRef = useRef(cloneDeep(displaySettings))
  displaySettingsRef.current = cloneDeep(displaySettings)

  // States
  const [isManualEditingPointSize, setIsManualEditingPointSize] = useState(false)
  const [isManualEditingPcdTransparency, setIsManualEditingPcdTransparency] = useState(false)
  const [isManualEditingMeshOpacity, setIsManualEditingMeshOpacity] = useState(false)
  const [isManualEditingLabelFontSize, setIsManualEditingLabelFontSize] = useState(false)

  // Derived states
  const meshUnavailableMessage = 'エリア設定からメッシュファイルをアップロードしてください'

  /**
   * Save inspection area changes.
   */
  const saveInspectionArea = useCallback(async () => {
    // Only allow if user has permissions and necessary data is present
    if (!isAllowedToModifyDisplaySettings) return
    if (!project || !inspectionArea) return

    try {
      dispatch(setIsSavingArea(true))

      const token = await getAccessToken()
      if (!token) throw new Error('No access token (SettingsPanel)')

      const result = await patchInspectionArea(
        token,
        project.project_id,
        {
          ...inspectionArea,
          display_config: displaySettingsRef.current,
        },
        showErrorModal,
      )

      if (!result) throw new Error('Failed to patch inspection area (SettingsPanel)')

      dispatch(patchInspectionAreas([result]))
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      dispatch(setIsSavingArea(false))
    }
  }, [project, inspectionArea, isAllowedToModifyDisplaySettings, getAccessToken, showErrorModal, dispatch])

  /**
   * Debounced version of saveInspectionArea.
   */
  const saveInspectionAreaDebounced = useDebouncedCallback(saveInspectionArea, 3000)

  useEffect(() => {
    const fn = () => saveInspectionAreaDebounced()
    document.addEventListener('inspection-area:patch', fn)

    return () => {
      document.removeEventListener('inspection-area:patch', fn)
    }
  }, [saveInspectionAreaDebounced])

  return (
    <>
      {/* Point Cloud Controls Group */}
      <Text fontWeight="bold" fontSize="sm" color="gray2.500" mt={2} mb={1}>
        点群
      </Text>

      <VStack pl={2} gap={1}>
        {/* Point size */}
        <HStack gap={0} w="100%">
          <Text flex={1} whiteSpace="nowrap">
            点のサイズ
          </Text>
          {pointSize !== undefined && (
            <RangeInput
              colorPalette="gray2Light"
              size="sm"
              key="pointSize"
              testId="pointSize"
              min={0.1}
              max={10}
              step={0.1}
              precision={1}
              onManualEdit={setIsManualEditingPointSize}
              value={pointSize}
              defaultValue={defaultDisplaySettings.pcd.point_size}
              displayValue={`${pointSize}`}
              updateValue={(newValue) => {
                if (newValue) {
                  dispatch(patchEditorDisplaySettings({ pcd: { point_size: newValue } }))
                  void saveInspectionAreaDebounced()
                }
              }}
              onReset={() => {
                dispatch(patchEditorDisplaySettings({ pcd: { point_size: defaultDisplaySettings.pcd.point_size } }))
                void saveInspectionAreaDebounced()
              }}
            />
          )}
        </HStack>

        {/* PCD transparency */}
        <HStack gap={0} w="100%">
          <Text mr={2}>不透明度</Text>
          <Spacer />
          {pcdTransparency !== undefined && (
            <RangeInput
              colorPalette="gray2Light"
              size="sm"
              key="pcdTransparency"
              testId="pcdTransparency"
              min={0}
              max={100}
              step={10}
              precision={0}
              value={pcdTransparency * 100}
              defaultValue={defaultDisplaySettings.pcd.opacity * 100}
              displayValue={`${pcdTransparency * 100}`}
              unit="%"
              onManualEdit={setIsManualEditingPcdTransparency}
              updateValue={(newValue) => {
                dispatch(patchEditorDisplaySettings({ pcd: { opacity: newValue / 100 } }))
                void saveInspectionAreaDebounced()
              }}
              onReset={() => {
                dispatch(patchEditorDisplaySettings({ pcd: { opacity: defaultDisplaySettings.pcd.opacity } }))
                void saveInspectionAreaDebounced()
              }}
            />
          )}
        </HStack>

        {/* Background color */}
        <HStack gap={1} w="100%">
          <Text mr={9}>背景色</Text>
          <Spacer />
          <Popover.Root>
            <Popover.Trigger asChild>
              <Button
                variant="outline"
                maxWidth={6}
                minWidth={6}
                width={6}
                height={5}
                p={0}
                backgroundColor={backgroundColor}
                borderColor="secondary.700"
                _hover={{ bg: backgroundColor }}
                _focus={{ bg: backgroundColor }}
                _active={{ bg: backgroundColor }}
              />
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content w="auto">
                  <Chrome
                    inputType={ChromeInputType.HEXA}
                    showAlpha={false}
                    showEyeDropper={false}
                    color={backgroundColor}
                    onChange={(color) => {
                      dispatch(patchEditorDisplaySettings({ general: { bg_color: color.hex } }))
                      void saveInspectionAreaDebounced()
                    }}
                  />
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
          <IconButton
            aria-label="reset"
            size="xs"
            fontSize="sm"
            onClick={(e) => {
              e.stopPropagation()
              dispatch(patchEditorDisplaySettings({ general: { bg_color: defaultDisplaySettings.general.bg_color } }))
              void saveInspectionAreaDebounced()
            }}
            variant="toolbarIcon"
            minW="auto"
            width="20px"
            height="20px"
          >
            <ResetIcon />
          </IconButton>
        </HStack>

        {/* Label font size */}
        <HStack gap={0} w="100%">
          {!isManualEditingLabelFontSize && <Text mr={2}>ラベルサイズ</Text>}
          <Spacer />
          {labelFontSize !== undefined && (
            <RangeInput
              colorPalette="gray2Light"
              size="sm"
              key="labelFontSize"
              testId="labelFontSize"
              min={1}
              max={5}
              step={0.1}
              precision={1}
              value={labelFontSize}
              defaultValue={defaultDisplaySettings.general.label_size}
              displayValue={`${labelFontSize}`}
              onManualEdit={setIsManualEditingLabelFontSize}
              updateValue={(newValue) => {
                dispatch(patchEditorDisplaySettings({ general: { label_size: newValue } }))
                void saveInspectionAreaDebounced()
              }}
              onReset={() => {
                dispatch(
                  patchEditorDisplaySettings({ general: { label_size: defaultDisplaySettings.general.label_size } }),
                )
                void saveInspectionAreaDebounced()
              }}
            />
          )}
        </HStack>

        {/* PCD visibility toggle */}
        <LayerItem
          invisible={!isPointCloudVisible}
          label="表示・非表示"
          updateVisibility={(visibility) => {
            dispatch(setIsPcdVisible(visibility))
          }}
        />
      </VStack>

      {/* Mesh Controls Group with toggle button (for demo) */}
      <Text fontWeight="bold" fontSize="sm" color="gray2.500" mt={3} mb={1}>
        3Dメッシュ
      </Text>

      <Box pl={2}>
        {/* Mesh opacity */}
        <Tooltip
          label={!isMeshFilePresent ? meshUnavailableMessage : undefined}
          showArrow
          positioning={{ placement: 'top' }}
          openDelay={300}
          /* TODO: style migration needed - w=100% */
          /* TODO: style migration needed - shouldWrapChildren=false */
        >
          <Box opacity={isMeshFilePresent ? 1 : 0.6} cursor={isMeshFilePresent ? 'default' : 'not-allowed'}>
            <HStack gap={0} w="100%">
              {!isManualEditingMeshOpacity && <Text mr={2}>不透明度</Text>}
              <Spacer />
              <RangeInput
                colorPalette="gray2Light"
                size="sm"
                key="meshOpacity"
                testId="meshOpacity"
                min={0}
                max={100}
                step={10}
                precision={0}
                value={solidMeshTransparency * 100}
                defaultValue={defaultDisplaySettings.mesh.opacity * 100}
                displayValue={`${solidMeshTransparency * 100}`}
                unit="%"
                onManualEdit={setIsManualEditingMeshOpacity}
                updateValue={(newValue) => {
                  dispatch(patchEditorDisplaySettings({ mesh: { opacity: newValue / 100 } }))
                  void saveInspectionAreaDebounced()
                }}
                onReset={() => {
                  dispatch(patchEditorDisplaySettings({ mesh: { opacity: defaultDisplaySettings.mesh.opacity } }))
                  void saveInspectionAreaDebounced()
                }}
                disabled={!isMeshFilePresent}
              />
            </HStack>

            {/* Mesh visibility toggle */}
            <LayerItem
              invisible={!isMeshFileVisible}
              label="表示・非表示"
              updateVisibility={(visible) => {
                if (!isMeshFilePresent) return
                dispatch(setIsVisible(visible))
              }}
              disabled={!isMeshFilePresent}
            />
          </Box>
        </Tooltip>
      </Box>
    </>
  )
}

export default Settings
